import type { IUtilizationDay } from '@apis/equipment';
import { formatDuration } from '../formatDuration';

/**
 * One weekly-utilization chart slot. `missing` fills the slot of a day that
 * failed to load, `loading` the slot of a day still on its way.
 */
export interface IUtilizationRow {
  date: string;
  axisLabel: string;
  pct: number;
  missing: number | null;
  loading: number | null;
  valueLabel: string;
  tipTitle: string;
  tipLines: string[];
}

export type Translate = (key: string, options?: Record<string, unknown>) => string;

export function toRows(days: IUtilizationDay[], todayDate: string | undefined, t: Translate, lang: string): IUtilizationRow[] {
  // `date` is already the site's calendar day (YYYY-MM-DD) — read it in UTC so
  // no zone shifts it to a neighbouring weekday.
  const weekdayFmt = new Intl.DateTimeFormat(lang, { weekday: 'short', timeZone: 'UTC' });
  const fullFmt = new Intl.DateTimeFormat(lang, { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
  return days.map((d) => {
    const at = new Date(`${d.date}T00:00:00Z`);
    const isToday = d.date === todayDate;
    return {
      date: d.date,
      axisLabel: isToday ? t('equipment.detail.today') : weekdayFmt.format(at),
      pct: d.hasData ? d.utilizationPct : 0,
      missing: d.unavailable ? 100 : null,
      loading: d.pending ? 100 : null,
      valueLabel: d.hasData ? `${d.utilizationPct}%` : '',
      tipTitle: isToday ? `${t('equipment.detail.today')} · ${fullFmt.format(at)}` : fullFmt.format(at),
      tipLines: tipLines(d, isToday, t),
    };
  });
}

function tipLines(d: IUtilizationDay, isToday: boolean, t: Translate): string[] {
  if (d.pending) return [t('equipment.detail.dayLoading')];
  if (d.unavailable) return [t('equipment.detail.dayUnavailable')];
  if (!d.hasData) return [t('equipment.detail.utilizationNoReadings')];
  const duration = formatDuration(d.activeMinutes, t);
  return [
    t(isToday ? 'equipment.detail.utilizationInUseSoFar' : 'equipment.detail.utilizationInUse', { duration }),
    t('equipment.detail.utilizationShare', { pct: d.utilizationPct }),
  ];
}
